import React from 'react';
import { FiLoader, FiAlertCircle } from 'react-icons/fi';

import type { AgentQualitySnapshotResponse } from '../../services/agentTrainingApi';

interface TrainStep2Props {
  snapshot: AgentQualitySnapshotResponse | null | undefined;
  error: Error | null;
}

/**
 * Step 2 — Exibe o progresso do treinamento enquanto roda em background.
 *
 * Consome snapshot atualizado via polling (1.5s) do `useSnapshot`.
 * Quando snapshot.status === 'completed', o container transiciona
 * automaticamente pro Step 3.
 *
 * Componentes visuais:
 * - Spinner grande
 * - Texto do `currentStepLabel` (ex: "Testando cenário 3/5: preço...")
 * - Barra de progresso baseada em `completedScenarios / totalScenarios`
 */
export const TrainStep2_Running: React.FC<TrainStep2Props> = ({
  snapshot,
  error,
}) => {
  if (error) {
    return (
      <div className="text-center py-8">
        <FiAlertCircle
          className="h-10 w-10 mx-auto mb-3"
          style={{ color: '#ef4444' }}
        />
        <div className="text-base font-semibold text-slate-800 mb-1">
          Erro ao rodar o treinamento
        </div>
        <p className="text-sm text-slate-500">{error.message}</p>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="text-center py-8">
        <FiLoader
          className="h-10 w-10 mx-auto mb-3 animate-spin"
          style={{ color: '#8b5cf6' }}
        />
        <div className="text-base font-semibold text-slate-800 mb-1">
          Preparando treinamento...
        </div>
      </div>
    );
  }

  const { completedScenarios, totalScenarios, currentStepLabel } =
    snapshot.progress;
  const percent =
    totalScenarios > 0
      ? Math.round((completedScenarios / totalScenarios) * 100)
      : 0;

  const statusLabel =
    snapshot.status === 'pending'
      ? 'Aguardando início'
      : snapshot.status === 'running'
        ? 'Em execução'
        : snapshot.status === 'failed'
          ? 'Erro'
          : 'Concluído';

  return (
    <div className="py-6">
      <div className="text-center mb-6">
        <FiLoader
          className="h-10 w-10 mx-auto mb-3 animate-spin"
          style={{ color: '#8b5cf6' }}
        />
        <div className="text-base font-semibold text-slate-800 mb-1">
          {statusLabel}
        </div>
        <p className="text-sm text-slate-500">{currentStepLabel}</p>
      </div>

      {/* Barra de progresso */}
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
          <span>
            {completedScenarios}/{totalScenarios} cenários
          </span>
          <span>{percent}%</span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <div
            className="h-full transition-all"
            style={{
              width: `${percent}%`,
              backgroundColor: '#8b5cf6',
            }}
          />
        </div>
      </div>

      {/* Cenários concluídos (lista curta pra feedback visual) */}
      {snapshot.scenarios.length > 0 && (
        <div className="max-w-md mx-auto mt-6">
          <div className="text-xs text-slate-500 mb-2">
            Cenários concluídos
          </div>
          <ul className="space-y-1">
            {snapshot.scenarios.map((s) => (
              <li
                key={s.scenarioId}
                className="text-xs flex items-center gap-2 text-slate-600"
              >
                <span style={{ color: '#10b981' }}>✓</span>
                <span>{s.scenarioLabel}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
